/**
 * A layout opened through a view-only link: the hall and every way of looking round it, the tape and
 * screenshots, and nothing that changes the layout. No sign-in.
 */

import type { HallResponse } from '@mawkeb/shared/manifest';
import { useEffect, useState } from 'react';
import { fetchView, renewViewAccess, type ViewResponse } from '../../api';
import { layoutState } from '../../state/layout';
import { useVenue, venue } from '../../state/store';
import { HallScreen } from '../HallScreen';
import { ago } from '../time';
import { StandInTag } from './Editor';

export function ViewPage({ token }: { token: string }) {
  const [view, setView] = useState<ViewResponse | null>(null);
  const [hall, setHall] = useState<HallResponse | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [gone, setGone] = useState<string | null>(null);
  const set = useVenue((s) => s.set);

  useEffect(() => {
    set({ editable: false, library: false });
    let cancelled = false;
    venue().setLoad({ phase: 'manifest', message: undefined });
    fetchView(token)
      .then((r) => {
        if (cancelled) return;
        layoutState().reset(r.layout.data);
        set({ manifest: r.hall.manifest, model: r.hall.manifest.model });
        venue().mark('manifest');
        setView(r);
        setHall(r.hall);
        if (r.offline) venue().toast('No connection: this is the layout as this device last saw it.');
      })
      .catch((e: Error & { kind?: string }) => {
        if (cancelled) return;
        if (e.kind === 'missing') setGone(e.message);
        else venue().setLoad({ phase: 'error', message: e.message });
      });
    return () => {
      cancelled = true;
    };
  }, [token, attempt, set]);

  if (gone) {
    return (
      <div className="message">
        <div className="panel card">
          <h1>This link doesn’t work any more</h1>
          <p>{gone}</p>
        </div>
      </div>
    );
  }

  const head = (
    <div className="where editor-head">
      <div className="names">
        <span className="layout-name" dir="auto">
          {view?.layout.name ?? 'Loading the layout…'}
        </span>
        <span className="event-name" dir="auto">
          {view ? `${view.event.name}. Saved by ${view.layout.savedBy} ${ago(view.layout.savedAt)}` : ''}
        </span>
      </div>
      <span className="tag quiet" title="You can look round and measure, not change anything.">
        View only
      </span>
      <StandInTag />
    </div>
  );

  return <HallScreen hall={hall} mode="view" onRetry={() => setAttempt((n) => n + 1)} renew={() => renewViewAccess(token)} head={head} />;
}
